import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext"; // ✅ NEW
import "../Styling/PastAttendanceModal.css";

const PastAttendanceModal = ({ isOpen, onClose, userId }) => {
  const today = new Date();
  const { user } = useAuth(); // ✅ NEW
  const orgId = user?.organisationId; // ✅ NEW

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [attendance, setAttendance] = useState([]);
  const [holidays, setHolidays] = useState({}); // ✅ NEW
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const years = Array.from({ length: 10 }, (_, i) => today.getFullYear() - i);

  const allMonths = [
    { label: "January", value: 1 },
    { label: "February", value: 2 },
    { label: "March", value: 3 },
    { label: "April", value: 4 },
    { label: "May", value: 5 },
    { label: "June", value: 6 },
    { label: "July", value: 7 },
    { label: "August", value: 8 },
    { label: "September", value: 9 },
    { label: "October", value: 10 },
    { label: "November", value: 11 },
    { label: "December", value: 12 }
  ];

  const months =
    year === today.getFullYear()
      ? allMonths.filter(m => m.value <= today.getMonth() + 1)
      : allMonths;

  /* ================= Fetch Monthly Attendance ================= */
  useEffect(() => {
    if (!isOpen || !userId) return;

    const fetchMonthlyAttendance = async () => {
      setLoading(true);
      setError("");
      setAttendance([]);

      try {
        const url = `${Base_URL}/Attendance/GetMonthlyAttendance?userId=${userId}&year=${year}&month=${month}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        setAttendance(
          Array.isArray(data)
            ? data.map(item => ({
                date: item.date,
                status: item.status,
                hours: item.hours
              }))
            : []
        );
      } catch (err) {
        console.error("❌ Monthly attendance error:", err);
        setError("Failed to load attendance");
      } finally {
        setLoading(false);
      }
    };

    fetchMonthlyAttendance();
  }, [isOpen, year, month, userId]);

  /* ================= Fetch Holidays ================= */
  useEffect(() => {
    if (!orgId || !isOpen) return;

    const fetchHolidays = async () => {
      try {
        const res = await fetch(
          `${Base_URL}/HolidayMasterOrg/GetHolidaysByOrg/${orgId}`
        );
        const data = await res.json();

        const holidayMap = {};
        data
          .filter(
            h =>
              h.holidayType === "National" ||
              h.holidayType === "Festival"
          )
          .forEach(h => {
            holidayMap[new Date(h.holidayDate).toDateString()] =
              h.holidayName;
          });

        setHolidays(holidayMap);
      } catch (err) {
        console.error("❌ Holiday fetch error:", err);
      }
    };

    fetchHolidays();
  }, [orgId, isOpen]);

  /* ================= CALCULATIONS ================= */
  const isWeekend = dateStr => {
    const d = new Date(dateStr).getDay();
    return d === 0 || d === 6;
  };

  const holidayCount = attendance.filter(day => {
    const key = new Date(day.date).toDateString();
    return holidays[key];
  }).length;

  const workingDays = attendance.filter(day => {
    const key = new Date(day.date).toDateString();
    return !isWeekend(day.date) && !holidays[key];
  });

  const presentCount = workingDays.filter(
    d =>
      d.status?.toLowerCase() === "present" ||
      d.status?.toLowerCase() === "wfh"
  ).length;

  const absentCount = workingDays.length - presentCount;

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={e => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h3>Monthly Attendance</h3>
          <X className="close-icon" onClick={onClose} />
        </div>

        {/* Filters */}
        <div className="filters">
          <select value={year} onChange={e => setYear(+e.target.value)}>
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <select value={month} onChange={e => setMonth(+e.target.value)}>
            {months.map(m => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {/* Body */}
        <div className="attendance-strip modal-strip">
          {loading && <p className="modal-msg">Loading...</p>}
          {!loading && error && <p className="modal-msg error">{error}</p>}
          {!loading && !error && attendance.length === 0 && (
            <p className="modal-msg">No attendance found</p>
          )}

          {!loading &&
            !error &&
            attendance.map((day, idx) => {
              const key = new Date(day.date).toDateString();
              const isHoliday = holidays[key];

              return (
                <div
                  key={idx}
                  className={`attendance-pill ${
                    isHoliday
                      ? "holiday"
                      : day.status.toLowerCase()
                  }`}
                  title={
                    isHoliday
                      ? `Holiday • ${holidays[key]}`
                      : `${day.date} • ${day.status} • ${day.hours}`
                  }
                >
                  <span className="pill-date">
                    {new Date(day.date).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short"
                    })}
                  </span>
                  <span className="pill-hours">
                    {isHoliday ? "Holiday" : day.hours}
                  </span>
                </div>
              );
            })}
        </div>

        {/* ✅ Bottom Summary */}
        <div className="attendance-legend modal-legend">
          <span>
            <i className="dot present"></i> Present: {presentCount}
          </span>
          <span>
            <i className="dot absent"></i> Absent: {absentCount}
          </span>
          <span>
            <i className="dot holiday"></i> Holiday: {holidayCount}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PastAttendanceModal;
